import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wheat, Sprout, Boxes, FlaskConical, Fuel, MapPin, AlertTriangle, Check } from "lucide-react";

const FARM_IMG =
  "https://static.prod-images.emergentagent.com/jobs/678604cc-f8b3-441a-bab0-503b63172c82/images/ae08432fd1ba7a0434f7e2a14d86efdd46400f3a5a03235d651f61d4e6fb08de.png";

const STORES = [
  {
    id: "grain",
    name: "Grain Store",
    type: "Grain",
    icon: Wheat,
    pos: { top: 27, left: 39.5 },
    contents: [
      { item: "Skyfall wheat", qty: "420 t", status: "ok", note: "14.2% — safe to store" },
      { item: "Planet barley", qty: "180 t", status: "warn", note: "15.8% — needs drying" },
    ],
  },
  {
    id: "fert",
    name: "Fertiliser Shed",
    type: "Fertiliser",
    icon: Sprout,
    pos: { top: 28.5, left: 67 },
    contents: [
      { item: "Nitram (34.5% N)", qty: "32 t", status: "ok" },
      { item: "Liquid N solution", qty: "6,500 L", status: "ok" },
    ],
  },
  {
    id: "bales",
    name: "Bale Stack",
    type: "Bale & straw",
    icon: Boxes,
    pos: { top: 71, left: 23 },
    contents: [
      { item: "Meadow bale", qty: "240 bales", status: "ok" },
      { item: "Barley straw", qty: "320 bales", status: "ok" },
    ],
  },
  {
    id: "chem",
    name: "Chemical Store",
    type: "Crop protection",
    icon: FlaskConical,
    pos: { top: 79, left: 52.5 },
    contents: [
      { item: "Glyphosate", qty: "600 L", status: "ok" },
      { item: "Fungicide", qty: "180 L", status: "ok" },
    ],
  },
  {
    id: "fuel",
    name: "Fuel Tanks",
    type: "Fuel",
    icon: Fuel,
    pos: { top: 65.5, left: 76.5 },
    contents: [
      { item: "Red diesel", qty: "4,200 L", status: "ok" },
      { item: "AdBlue", qty: "800 L", status: "warn", note: "Running low" },
    ],
  },
];

export const FarmMap = () => {
  const [active, setActive] = useState(STORES[0].id);
  const current = STORES.find((s) => s.id === active);

  return (
    <div
      data-testid="farm-map"
      className="grid gap-6 overflow-hidden rounded-[24px] border border-[#E8E5DF] bg-white p-3 shadow-sm lg:grid-cols-5 lg:p-4"
    >
      {/* MAP */}
      <div className="relative lg:col-span-3">
        <div className="relative h-[320px] overflow-hidden rounded-[18px] sm:h-[440px]">
          <img
            src={FARM_IMG}
            alt="Aerial view of the farmyard"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#14401F]/25 to-transparent" />

          {STORES.map((s) => {
            const Icon = s.icon;
            const isActive = s.id === active;
            return (
              <button
                key={s.id}
                data-testid={`map-pin-${s.id}`}
                onClick={() => setActive(s.id)}
                style={{ top: `${s.pos.top}%`, left: `${s.pos.left}%` }}
                className="absolute z-10 -translate-x-1/2 -translate-y-1/2 focus:outline-none"
                aria-label={s.name}
              >
                <span className="relative flex flex-col items-center">
                  {isActive && (
                    <span className="absolute -top-1 h-11 w-11 animate-ping rounded-full bg-[#2FA84F]/40" />
                  )}
                  <span
                    className={`relative flex h-9 w-9 items-center justify-center rounded-full border-2 border-white shadow-lg transition-transform duration-200 ${
                      isActive ? "scale-110 bg-[#2FA84F]" : "bg-[#14401F] hover:scale-105"
                    }`}
                  >
                    <Icon className="h-4 w-4 text-white" />
                  </span>
                  <span
                    className={`mt-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold shadow-md transition-colors ${
                      isActive ? "bg-[#2FA84F] text-white" : "bg-white/95 text-[#1A1C18]"
                    }`}
                  >
                    {s.name}
                  </span>
                </span>
              </button>
            );
          })}

          <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-medium text-[#14401F] backdrop-blur-sm">
            <MapPin className="h-3.5 w-3.5" /> Tap a store to see what's inside
          </div>
        </div>
      </div>

      {/* DETAILS */}
      <div className="lg:col-span-2">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            data-testid="map-store-details"
            className="flex h-full flex-col rounded-[18px] bg-[#F5F2ED] p-5 sm:p-6"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#14401F]">
                <current.icon className="h-5 w-5 text-white" />
              </span>
              <div>
                <h4 className="text-lg font-bold leading-tight text-[#14401F]">{current.name}</h4>
                <span className="text-xs font-semibold uppercase tracking-wider text-[#8A9A7B]">
                  {current.type}
                </span>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              {current.contents.map((c) => (
                <div key={c.item} className="rounded-xl border border-[#E8E5DF] bg-white p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#1A1C18]">{c.item}</span>
                    <span className="text-sm font-bold text-[#14401F]">{c.qty}</span>
                  </div>
                  {c.note && (
                    <div
                      className={`mt-1.5 flex items-center gap-1.5 text-xs font-medium ${
                        c.status === "warn" ? "text-[#C85A3C]" : "text-[#4CAF50]"
                      }`}
                    >
                      {c.status === "warn" ? (
                        <AlertTriangle className="h-3.5 w-3.5" />
                      ) : (
                        <Check className="h-3.5 w-3.5" />
                      )}
                      {c.note}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <p className="mt-auto pt-5 text-xs leading-relaxed text-[#1A1C18]/55">
              Live balances update as your team logs loads in and out.
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
